/**
 * Connection / reconnect overlay. Three mutually-exclusive states:
 *  - reconnecting:    WE dropped and are retrying our seat.
 *  - opponentWaiting: the OPPONENT dropped; we wait out their grace window.
 *  - lost:            terminal — the reconnect window expired, match over.
 *
 * Sits just below the MatchEndOverlay (z-index) so a settled result always
 * wins the screen. The terminal state posts an exit request to the
 * embedding web app.
 */
export class ConnectionOverlay {
  private root: HTMLDivElement;
  private countdownTimer: number | null = null;
  /** Which state is on screen — drives the snapshot-driven self-heal. */
  private mode: "none" | "reconnecting" | "waiting" | "lost" = "none";

  constructor() {
    const el = document.createElement("div");
    el.id = "connection-overlay";
    el.style.cssText = [
      "position:fixed",
      "inset:0",
      "z-index:9998",
      "display:none",
      "flex-direction:column",
      "align-items:center",
      "justify-content:center",
      "gap:14px",
      "text-align:center",
      "background:rgba(6,8,12,0.78)",
      "backdrop-filter:blur(4px)",
      "font-family:system-ui,-apple-system,'Segoe UI',Roboto,sans-serif",
      "color:#fff",
      // The reconnecting/waiting states are purely informational — they must
      // NEVER eat clicks. A stuck overlay (missed opponent_resumed while our
      // own socket blinked) used to swallow every buy-menu click underneath:
      // menu visible, money visible, clicks dead (mainnet 2026-07-02, room
      // rcbu-qlTt). Only the terminal state's button opts back into events.
      "pointer-events:none",
    ].join(";");
    document.body.appendChild(el);
    this.root = el;
  }

  /** WE dropped — show a reconnecting spinner. */
  showReconnecting(): void {
    this.mode = "reconnecting";
    this.clearCountdown();
    this.root.innerHTML = `
      <div style="font-size:30px;font-weight:700;color:#fbbf24">Connection lost</div>
      <div style="font-size:17px;opacity:0.85">Reconnecting…</div>
      <div style="margin-top:6px;width:34px;height:34px;border:3px solid rgba(255,255,255,0.25);border-top-color:#fff;border-radius:50%;animation:co-spin 0.8s linear infinite"></div>
      <style>@keyframes co-spin{to{transform:rotate(360deg)}}</style>
    `;
    this.reveal();
  }

  /** OPPONENT dropped — count down their grace window. */
  showOpponentWaiting(graceSec: number): void {
    this.mode = "waiting";
    this.clearCountdown();
    let left = Math.max(0, Math.floor(graceSec));
    const render = () => {
      this.root.innerHTML = `
        <div style="font-size:30px;font-weight:700;color:#fbbf24">Opponent disconnected</div>
        <div style="font-size:17px;opacity:0.85">Waiting for them to reconnect… <b>${left}s</b></div>
      `;
    };
    render();
    this.reveal();
    this.countdownTimer = window.setInterval(() => {
      left -= 1;
      if (left <= 0) this.clearCountdown();
      render();
    }, 1000);
  }

  /** Terminal — we never made it back. Offer a way out. */
  showLost(message = "You didn't reconnect in time."): void {
    this.mode = "lost";
    this.clearCountdown();
    this.root.innerHTML = `
      <div style="font-size:34px;font-weight:800;color:#f87272">MATCH LOST</div>
      <div style="font-size:16px;opacity:0.85">${message}</div>
      <button id="co-exit" style="margin-top:12px;padding:11px 26px;font-size:15px;font-weight:600;border:0;border-radius:10px;background:#5b8cff;color:#fff;cursor:pointer;pointer-events:auto">Back to lobby</button>
    `;
    this.reveal();
    this.root
      .querySelector<HTMLButtonElement>("#co-exit")
      ?.addEventListener("click", () => {
        try {
          window.parent?.postMessage({ type: "hupper-fps-exit" }, "*");
        } catch {
          /* standalone — no embedding parent */
        }
      });
  }

  hide(): void {
    this.mode = "none";
    this.clearCountdown();
    this.root.style.display = "none";
    this.root.innerHTML = "";
  }

  /**
   * Snapshot-driven self-heal — the one-shot messages that normally clear
   * this overlay (onReconnected / opponent_resumed) are LOST if they fire
   * while our own socket is down, leaving the overlay up forever. State
   * patches re-sync on reconnect, so drive the recovery off them instead:
   * any HUD snapshot means WE are connected (clear "reconnecting"); a fresh
   * buy phase means a new round started, which the server only does with the
   * match resolved-or-live (clear "waiting"). The terminal state stays.
   */
  selfHeal(phase: string): void {
    if (this.mode === "reconnecting") this.hide();
    else if (this.mode === "waiting" && phase === "buy") this.hide();
  }

  private reveal(): void {
    this.root.style.display = "flex";
    // Free the mouse so any button is clickable + the player isn't stuck
    // looking around a frozen world.
    try {
      document.exitPointerLock?.();
    } catch {
      /* ignore */
    }
  }

  private clearCountdown(): void {
    if (this.countdownTimer !== null) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }
}
